import { CheckCircle2, ExternalLink, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import BookingTermsPanel from "../BookingTermsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => "₱" + Number(n || 0).toLocaleString("en-PH");
const isoLocal = (d) => (d ? new Date(d).toISOString() : "");

// Read directly off window.location rather than useSearchParams(): this
// component is also unit-tested outside a Router, and prefill is read-only
// (no navigation), so a Router dependency isn't needed either way.
function readPrefill() {
  const p = new URLSearchParams(window.location.search);
  const fragment = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
  const value = (key) => fragment.get(key) || p.get(key) || "";
  return {
    vehicle: value("vehicle"), name: value("name"),
    email: value("email"), phone: value("phone"),
    notes: value("notes"),
    // Phase 3A item 4: present only when this page was reached via the
    // widget's "Continue on ConfirmPH" link (see hostedUrl() below). The
    // server independently validates this before trusting it for
    // attribution; it is never taken at face value.
    handoff: value("handoff"),
    // The parent-page loader obtains this short-lived proof through a CORS
    // request whose Origin header is supplied by the browser. A fragment is
    // used so the opaque value is not sent in the iframe's initial request,
    // Referer header, server access log, or browsing history query string.
    widgetSession: fragment.get("widget_session") || "",
  };
}

export default function BookingIntegrationShell({ identifier, embedded = false }) {
  const [prefill] = useState(readPrefill);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const [vehicleId, setVehicleId] = useState(prefill.vehicle);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [availability, setAvailability] = useState(null);
  const [checking, setChecking] = useState(false);
  const [form, setForm] = useState({
    name: prefill.name, email: prefill.email,
    phone: prefill.phone, notes: prefill.notes,
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);
  const [widgetHandoffToken, setWidgetHandoffToken] = useState(null);
  const idempotencyKeyRef = useRef(null);

  useEffect(() => {
    idempotencyKeyRef.current = null;
  }, [vehicleId, start, end, form, consent]);

  const loadData = useCallback(() => axios
    .get(`${API}/public/business/integration/${encodeURIComponent(identifier)}`)
    .then((r) => { setData(r.data); return r.data; })
    .catch((e) => {
      setError(e?.response?.status === 404 ? "Integration not found." : "Could not load this booking page.");
      return null;
    }), [identifier]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier]);

  // In embedded mode, privileged actions require a server-minted browser-
  // origin session delivered by the parent loader. The iframe never treats
  // document.referrer, ancestorOrigins, a query parameter, or a client-
  // supplied origin string as authorization.
  const widgetEmbedVerified = !embedded
    || (!!data?.allowed_embed_origin && !!prefill.widgetSession);

  // Phase 3A item 4: the widget mints its own single-use handoff token as
  // soon as it knows widget mode is actually enabled, so the "Continue on
  // ConfirmPH" link always has one ready. Minting is best-effort: if it
  // fails, hostedUrl() just omits it and the eventual booking is attributed
  // as hosted_page instead of widget_handoff, never a blocking error.
  // Phase 3A item 6, but it is still a privileged widget action, so it
  // never even attempts to mint without a verified embedding origin.
  useEffect(() => {
    if (!embedded || !data?.widget_enabled || !widgetEmbedVerified) return;
    let active = true;
    axios.post(`${API}/public/business/integration/${encodeURIComponent(identifier)}/handoff`,
      { widget_session_token: prefill.widgetSession })
      .then((r) => { if (active) setWidgetHandoffToken(r.data?.handoff_token || null); })
      .catch(() => {});
    return () => { active = false; };
  }, [embedded, identifier, data?.widget_enabled, widgetEmbedVerified, prefill.widgetSession]);

  useEffect(() => {
    if (!embedded || !data?.allowed_embed_origin || !window.ResizeObserver) return undefined;
    if (!prefill.widgetSession) return undefined;
    const observer = new ResizeObserver(() => {
      window.parent.postMessage(
        { type: "confirmph:resize", height: document.documentElement.scrollHeight },
        data.allowed_embed_origin,
      );
    });
    observer.observe(document.documentElement);
    return () => observer.disconnect();
  }, [embedded, data, error, prefill.widgetSession]);

  const vehicles = data?.vehicles || [];
  const bookable = !!data?.enable_in_app_bookings;

  const checkAvailability = useCallback(async () => {
    if (!bookable || !vehicleId || !start || !end) { setAvailability(null); return; }
    setChecking(true); setAvailability(null);
    try {
      const { data: a } = await axios.post(
        `${API}/public/business/integration/${encodeURIComponent(identifier)}/availability`,
        { item_id: vehicleId, start_datetime: isoLocal(start), end_datetime: isoLocal(end) },
      );
      setAvailability(a);
    } catch {
      setAvailability(null);
    } finally {
      setChecking(false);
    }
  }, [identifier, bookable, vehicleId, start, end]);

  useEffect(() => { checkAvailability(); }, [checkAvailability]);

  // Builds the URL to the full ConfirmPH hosted page, carrying over whatever
  // the customer already typed so they don't re-enter it. Used both as the
  // widget's post-inquiry "continue" link and as the loader's own fallback
  // when the iframe can't load at all (frontend/public/embed/v1/widget.js).
  const hostedUrl = useCallback(() => {
    const p = new URLSearchParams();
    if (vehicleId) p.set("vehicle", vehicleId);
    if (form.name) p.set("name", form.name);
    if (form.email) p.set("email", form.email);
    if (form.phone) p.set("phone", form.phone);
    if (form.notes) p.set("notes", form.notes);
    // Phase 3A item 4: carries the server-minted handoff token so the
    // hosted page's eventual booking can be attributed as widget_handoff.
    // Omitted entirely (not a fake/empty value) when minting hasn't
    // finished yet: the hosted page just falls back to hosted_page then.
    if (widgetHandoffToken) p.set("handoff", widgetHandoffToken);
    const serialized = p.toString();
    // Widget-prefill values can contain customer PII and the handoff token.
    // Keep them in the fragment so they are not sent in navigation requests,
    // referrers, access logs, or query-string history. Direct hosted links
    // retain their existing query-string compatibility.
    const suffix = serialized ? `${embedded ? "#" : "?"}${serialized}` : "";
    return `${window.location.origin}/book/${encodeURIComponent(identifier)}${suffix}`;
  }, [embedded, identifier, vehicleId, form, widgetHandoffToken]);

  const submitInquiry = async () => {
    const { data: r } = await axios.post(
      `${API}/public/business/integration/${encodeURIComponent(identifier)}/inquiry`,
      {
        customer_name: form.name, customer_email: form.email,
        customer_phone: form.phone || null, vehicle_id: vehicleId || null,
        start_datetime: start ? isoLocal(start) : null,
        end_datetime: end ? isoLocal(end) : null,
        notes: form.notes || null, consent: true,
        source: embedded ? "widget" : "hosted",
        // Required server-side for widget inquiries; omitted in hosted mode.
        widget_session_token: embedded ? prefill.widgetSession : undefined,
      },
    );
    return r;
  };

  const submitBooking = async () => {
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = globalThis.crypto?.randomUUID?.()
        || `wi-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    // Phase 3A item 4: carries the handoff token (if this page was
    // reached via the widget's "Continue on ConfirmPH" link) so the server
    // can attribute this booking as widget_handoff after validating it.
    // Never asserted as a "channel" directly: the server decides.
    const bookUrl = `${API}/public/business/integration/${encodeURIComponent(identifier)}/book`;
    const headers = { "Idempotency-Key": idempotencyKeyRef.current };
    if (prefill.handoff) headers["X-ConfirmPH-Handoff-Token"] = prefill.handoff;
    const { data: r } = await axios.post(
      bookUrl,
      {
        item_id: vehicleId, start_datetime: isoLocal(start), end_datetime: isoLocal(end),
        customer_name: form.name, customer_email: form.email,
        customer_phone: form.phone || null, customer_notes: form.notes || null,
        accepted_terms: true, accepted_terms_version_id: data?.current_terms?.id,
      },
      { headers },
    );
    return r;
  };

  // Phase 3A item 5, which channel-specific submission field gates a real
  // booking must be picked explicitly per mode, never inferred from the
  // other mode's value. `widget_booking_submission_enabled` stays false
  // until a dedicated widget-booking origin actually ships, so this keeps
  // real in-frame booking impossible even if the `embedded`-based early
  // gate above were ever bypassed.
  const canSubmitRealBooking = embedded
    ? !!data?.widget_booking_submission_enabled
    : !!data?.hosted_booking_submission_enabled;
  // A real booking (as opposed to an inquiry) requires the customer to have
  // actually seen and accepted a real published terms version, never a
  // generic unseen "their booking terms" checkbox.
  const isRealBookingAttempt = canSubmitRealBooking && bookable && vehicleId && start && end;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !consent) return;
    if (isRealBookingAttempt && !data?.current_terms) return;
    setSubmitting(true); setSubmitError("");
    try {
      // A real, lock-protected reservation only ever happens on the hosted
      // (non-embedded) full-page origin. The embedded widget always submits
      // the lightweight inquiry instead and hands off to the hosted page to
      // complete an actual booking: see BookingIntegrationShell's module
      // docstring / ConnectExistingWebsite.jsx for why: there is no
      // dedicated widget origin yet, so real submission stays out of the
      // iframe by design, not by network-level enforcement.
      if (isRealBookingAttempt) {
        const r = await submitBooking();
        setResult({ kind: "booking", ...r });
      } else {
        const r = await submitInquiry();
        setResult({ kind: "inquiry", ...r });
      }
    } catch (err) {
      if (err?.response?.status === 409) idempotencyKeyRef.current = null;
      const detail = err?.response?.data?.detail;
      if (detail?.code === "terms_version_stale" || detail?.code === "no_published_terms") {
        // Do not silently accept the new/missing version: clear prior
        // acceptance, re-fetch, and make the customer review it again.
        setConsent(false);
        idempotencyKeyRef.current = null;
        await loadData();
      }
      setSubmitError((typeof detail === "string" ? detail : detail?.message)
        || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <ShellFrame embedded={embedded}>
        <StateMessage title={error} />
        <FallbackLink identifier={identifier} />
      </ShellFrame>
    );
  }
  if (!data) return <ShellFrame embedded={embedded}><StateMessage title="Loading booking experience…" /></ShellFrame>;

  // Phase 3A item 5: each mode reads its own explicit capability field so
  // neither can be inferred from the other (hosted_enabled/widget_enabled
  // still exist on `data` too, but the *_submission_enabled fields are the
  // ones the public contract actually guarantees are independently truthful).
  const enabled = embedded ? data.widget_inquiry_submission_enabled : data.hosted_booking_submission_enabled;
  if (!enabled) {
    return (
      <ShellFrame embedded={embedded}>
        <StateMessage title="This booking integration is not enabled." />
        <FallbackLink identifier={identifier} />
      </ShellFrame>
    );
  }

  // Phase 3A item 6: a minimal, non-interactive safe shell: the widget is
  // enabled for this business, but this specific embed couldn't prove it's
  // running on the business's verified website. No inquiry form, no
  // handoff: only the always-safe top-level fallback link. Covers a
  // missing referrer (suppressed by the embedding page), a malformed one,
  // and a genuine mismatch identically: none of them are trusted enough
  // to unlock a privileged action.
  if (!widgetEmbedVerified) {
    return (
      <ShellFrame embedded={embedded}>
        <StateMessage title="This booking widget isn't authorized for this website." />
        <FallbackLink identifier={identifier} />
      </ShellFrame>
    );
  }

  if (result) {
    return (
      <ShellFrame embedded={embedded} appearance={data.appearance}>
        <div className="space-y-4 text-center py-6" data-testid="booking-shell-result">
          <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
          <h2 className="text-xl font-bold">
            {result.kind === "booking" ? "Request received" : "Thanks: we got your request"}
          </h2>
          <p className="text-sm text-slate-600">
            {result.kind === "booking"
              ? result.message
              : "The business will follow up with you shortly to confirm details."}
          </p>
          {result.kind === "booking" && result.booking_reference && (
            <p className="text-xs text-slate-500">Reference: {result.booking_reference}</p>
          )}
          {embedded && (
            <a href={hostedUrl()} target="_blank" rel="noopener noreferrer"
               data-testid="booking-shell-continue-link"
               className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-semibold">
              Continue on ConfirmPH to complete your booking <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </ShellFrame>
    );
  }

  return (
    <ShellFrame embedded={embedded} appearance={data.appearance}>
      <form className="space-y-4" onSubmit={handleSubmit}
            data-testid={embedded ? "embedded-booking-shell" : "hosted-booking-shell"}>
        <div>
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
            <ShieldCheck className="w-4 h-4" /> Securely hosted by ConfirmPH
          </div>
          <h1 className={`${embedded ? "text-xl" : "text-2xl sm:text-3xl"} font-bold mt-2`}>
            {data.business?.name || "Book with us"}
          </h1>
          {data.business?.city && <p className="text-sm text-slate-600 mt-1">{data.business.city}</p>}
        </div>

        {vehicles.length > 0 && (
          <Field label="Vehicle" testId="wi-vehicle" as="select"
                 value={vehicleId} onChange={setVehicleId}>
            <option value="">Not sure yet / any vehicle</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}{v.price_per_day ? ` · ${fmt(v.price_per_day)}/day` : ""}
              </option>
            ))}
          </Field>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Pickup" testId="wi-start" type="datetime-local" value={start} onChange={setStart} />
          <Field label="Return" testId="wi-end" type="datetime-local" value={end} onChange={setEnd} />
        </div>

        {bookable && vehicleId && start && end && (
          <div className="text-xs text-slate-600" data-testid="wi-availability">
            {checking ? "Checking availability…" : availability ? (
              availability.available
                ? `Available: est. ${fmt(availability.price_estimate)} (${availability.days} day${availability.days === 1 ? "" : "s"})`
                : `Not available: ${availability.reason || "these dates"}`
            ) : null}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Your name" testId="wi-name" value={form.name}
                 onChange={(v) => setForm((f) => ({ ...f, name: v }))} required />
          <Field label="Email" testId="wi-email" type="email" value={form.email}
                 onChange={(v) => setForm((f) => ({ ...f, email: v }))} required />
        </div>
        <Field label="Phone (optional)" testId="wi-phone" value={form.phone}
               onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
        <Field label="Anything else? (optional)" testId="wi-notes" as="textarea"
               value={form.notes} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} />

        {isRealBookingAttempt && (
          data.current_terms ? (
            <BookingTermsPanel terms={data.current_terms} />
          ) : (
            <p className="text-xs text-rose-700" data-testid="wi-no-terms-notice">
              Online booking is not available yet because this business has not published its ConfirmPH booking agreement.
            </p>
          )
        )}

        <label className="flex items-start gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={consent} data-testid="wi-consent"
                 onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
          I agree to share these details with the business
          {isRealBookingAttempt && data.current_terms
            ? ` and accept the Booking Agreement (Version ${data.current_terms.version_number}) above`
            : ""}.
        </label>

        {submitError && <p className="text-xs text-rose-700" data-testid="wi-submit-error">{submitError}</p>}

        <button type="submit"
                disabled={submitting || !form.name || !form.email || !consent
                  || (isRealBookingAttempt && !data.current_terms)}
                data-testid="wi-submit"
                className="w-full px-4 py-2.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold disabled:opacity-50">
          {submitting ? "Sending…" : (data.button_text || "Book Now")}
        </button>

        <p className="text-[11px] text-slate-500 text-center">
          {!embedded && bookable
            ? "Submitting with a vehicle and dates reserves it for the business to review."
            : "This sends a request: the business will follow up to confirm."}
        </p>
      </form>
    </ShellFrame>
  );
}

function ShellFrame({ children, embedded, appearance }) {
  const dark = appearance === "dark";
  return (
    <main className={`${embedded ? "min-h-[320px] p-4" : "min-h-screen p-4 sm:p-8 grid place-items-center"} ${dark ? "bg-slate-950" : "bg-slate-50"}`}>
      <section className={`w-full ${embedded ? "max-w-2xl" : "max-w-3xl"} rounded-2xl border p-5 sm:p-7 shadow-sm ${dark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-slate-200 text-slate-950"}`}>
        {children}
      </section>
    </main>
  );
}

function StateMessage({ title }) {
  return <div className="py-10 text-center text-sm text-slate-600">{title}</div>;
}

// The customer should never be stuck with a broken widget: this mirrors
// the same "Open secure booking page" fallback frontend/public/embed/v1/widget.js
// already renders outside the iframe if it can't mount at all.
function FallbackLink({ identifier }) {
  return (
    <div className="text-center pb-2">
      <a href={`${window.location.origin}/book/${encodeURIComponent(identifier)}`}
         target="_blank" rel="noopener noreferrer" data-testid="booking-shell-fallback-link"
         className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:underline">
        <ExternalLink className="w-3.5 h-3.5" /> Open ConfirmPH
      </a>
    </div>
  );
}

function Field({ label, value, onChange, testId, type, required, as, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {as === "select" ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
                className="mt-1 w-full h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm">
          {children}
        </select>
      ) : as === "textarea" ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
                  className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      ) : (
        <input type={type || "text"} value={value} onChange={(e) => onChange(e.target.value)}
               required={required} data-testid={testId}
               className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm" />
      )}
    </label>
  );
}
